import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./SuperAdminFrozenOrders.css";

/* ══════════════════════════════════════════════════
   API CONFIG
══════════════════════════════════════════════════ */
const RAW_API_BASE = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || "";
const CLEAN_API_BASE = String(RAW_API_BASE || "").trim().replace(/\/+$/, "");
const API_URL = CLEAN_API_BASE
  ? CLEAN_API_BASE.endsWith("/api")
    ? CLEAN_API_BASE
    : `${CLEAN_API_BASE}/api`
  : "/api";

const SUPERADMIN_API = `${API_URL}/superadmin`;
const ADMIN_API = `${API_URL}/admin`;
const RESERVATIONS_API = `${API_URL}/reservations`; // ✅ Added reference to the reservations endpoint
const STOCK_API = `${API_URL}/admin/stock`; // ✅ Added reference to stock endpoint

/* Map frozen-order addon labels → stock categories */
const addonCategoryToStock = (categoryLabel) => {
  const s = String(categoryLabel || "").toLowerCase();
  if (s.includes("cake")) return "cakes";
  if (s.includes("decor")) return "decorations";
  if (s.includes("gift")) return "gifts";
  if (s.includes("service")) return "services";
  return undefined; // e.g. 3D Glasses accessory — skip if not in stock
};

/* ══════════════════════════════════════════════════
   CONSTANTS
══════════════════════════════════════════════════ */
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const ADVANCE_PAID = 700;
const DEFAULT_FIXED_DECOR = 700;
const DEFAULT_EXTRA_ADULT_PRICE = 400;
const DEFAULT_KID_PRICE = 200;
const DEFAULT_3D_GLASS_PRICE = 300;
const STOCK_STORAGE_KEY = "stock_management_data";
const STOCK_EVENTS_KEY = "stock_events";
const CONFIRMED_ORDERS_KEY = "confirmed_frozen_orders";

/* ══════════════════════════════════════════════════
   HELPERS
══════════════════════════════════════════════════ */
const n = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const money = (value) => `₹${n(value).toLocaleString("en-IN")}`;

const safeQty = (item) => Math.max(1, Math.floor(n(item?.qty || item?.quantity || 1)));
const itemTotal = (item) => n(item?.price) * safeQty(item);

const getOrderId = (order) =>
  order?._id || order?.id || order?.orderId || order?.bookingId || "";

const formatTS = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toInputVal = (ddmmyyyy) => {
  if (!ddmmyyyy) return "";
  const p = String(ddmmyyyy).split("/");
  if (p.length !== 3) return "";
  return `${p[2]}-${p[1].padStart(2, "0")}-${p[0].padStart(2, "0")}`;
};

const fromInputVal = (yyyymmdd) => {
  if (!yyyymmdd) return "";
  const p = String(yyyymmdd).split("-");
  if (p.length !== 3) return "";
  return `${p[2]}/${p[1]}/${p[0]}`;
};

const formatRangeLabel = (from, to) => {
  const fmt = (s) => {
    if (!s) return "";
    const p = s.split("/");
    return `${p[0]} ${MONTH_NAMES[parseInt(p[1], 10) - 1] || ""} ${p[2]}`;
  };
  if (from && to) return `${fmt(from)} – ${fmt(to)}`;
  if (from) return `From ${fmt(from)}`;
  if (to) return `Until ${fmt(to)}`;
  return "All Dates";
};

const normalizeCatalogItem = (item) => ({
  id: item?._id || item?.id || item?.slug || item?.name,
  _id: item?._id,
  name: item?.name || item?.title || "Untitled",
  price: n(item?.price),
});

const getAddonArrays = (o) => ({
  cakes: Array.isArray(o?.cakes) ? o.cakes : [],
  extraDecors: Array.isArray(o?.extraDecors) ? o.extraDecors : [],
  gifts: Array.isArray(o?.gifts) ? o.gifts : [],
  services: Array.isArray(o?.services) ? o.services : [],
  extraAddons: Array.isArray(o?.extraAddons) ? o.extraAddons : [], // ✅ NEW
});

const computeSubtotalBeforeManualAdjustments = (o) => {
  const a = getAddonArrays(o);
  let total = n(o?.basePrice) + n(o?.fixedDecor || DEFAULT_FIXED_DECOR);

  total += Math.max(0, n(o?.extraAdults)) * n(o?.extraAdultPrice || DEFAULT_EXTRA_ADULT_PRICE);
  total += Math.max(0, n(o?.kids)) * n(o?.kidPrice || DEFAULT_KID_PRICE);

  total +=
    Math.max(0, n(o?.glassesCount)) *
    (n(o?.glass3DCost)
      ? n(o?.glass3DCost) / Math.max(1, n(o?.glassesCount))
      : DEFAULT_3D_GLASS_PRICE);

  a.cakes.forEach((x) => (total += itemTotal(x)));
  a.extraDecors.forEach((x) => (total += itemTotal(x)));
  a.gifts.forEach((x) => (total += itemTotal(x)));
  a.services.forEach((x) => (total += itemTotal(x)));
  a.extraAddons.forEach((x) => (total += itemTotal(x))); // ✅ NEW

  return total;
};

const computeTotal = (o) => {
  const base = computeSubtotalBeforeManualAdjustments(o);
  const foodCost = n(o?.foodCost || o?.paymentInfo?.foodCost);
  const manualDiscount = n(o?.manualDiscount || o?.paymentInfo?.manualDiscount);
  const couponDiscount = n(o?.discount);
  return Math.max(0, base + foodCost - manualDiscount - couponDiscount);
};

const buildAddonRows = (o) => {
  const a = getAddonArrays(o);

  const rows = [
    ...a.cakes.map((x) => ({ ...x, emoji: "🎂", category: "Cake" })),
    ...a.extraDecors.map((x) => ({ ...x, emoji: "🎨", category: "Decor" })),
    ...a.gifts.map((x) => ({ ...x, emoji: "🎁", category: "Gift" })),
    ...a.services.map((x) => ({ ...x, emoji: "🛎️", category: "Service" })),
    ...a.extraAddons.map((x) => ({
      ...x,
      emoji: x.emoji || "⭐",
      category: "Extra Add-on", // ✅ NEW — keeps it distinct in ConfirmOrderModal / add-on chips too
    })),
  ];

  if (n(o?.glassesCount) > 0) {
    rows.push({
      name: "3D Glasses",
      price: DEFAULT_3D_GLASS_PRICE,
      qty: n(o.glassesCount),
      emoji: "🥽",
      category: "Accessory",
    });
  }

  return rows;
};

const escapeHTML = (str) =>
  String(str ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

/* ══════════════════════════════════════════════════
   LOCALSTOCK HELPERS (client-side stock management)
══════════════════════════════════════════════════ */
const loadLocalStock = () => {
  try {
    const stored = localStorage.getItem(STOCK_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (e) {
    console.error("Failed to load stock from localStorage:", e);
  }
  return [];
};

const saveLocalStock = (data) => {
  try {
    localStorage.setItem(STOCK_STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.error("Failed to save stock to localStorage:", e);
  }
};

const loadStockEvents = () => {
  try {
    const stored = localStorage.getItem(STOCK_EVENTS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (e) {
    console.error("Failed to load stock events:", e);
  }
  return [];
};

const saveStockEvents = (events) => {
  try {
    localStorage.setItem(STOCK_EVENTS_KEY, JSON.stringify(events));
  } catch (e) {
    console.error("Failed to save stock events:", e);
  }
};

/* ══════════════════════════════════════════════════
   CONFIRMED ORDERS PERSISTENCE
══════════════════════════════════════════════════ */
const loadConfirmedOrders = () => {
  try {
    const stored = localStorage.getItem(CONFIRMED_ORDERS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) return new Set(parsed);
    }
  } catch (e) {
    console.error("Failed to load confirmed orders from localStorage:", e);
  }
  return new Set();
};

const saveConfirmedOrders = (ordersSet) => {
  try {
    localStorage.setItem(CONFIRMED_ORDERS_KEY, JSON.stringify([...ordersSet]));
  } catch (e) {
    console.error("Failed to save confirmed orders to localStorage:", e);
  }
};

/* ══════════════════════════════════════════════════
   TOAST NOTIFICATION COMPONENT
══════════════════════════════════════════════════ */
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`sfa-toast sfa-toast--${type}`}>
      <span className="sfa-toast__icon">
        {type === "success" ? "✅" : type === "error" ? "❌" : "ℹ️"}
      </span>
      <span className="sfa-toast__message">{message}</span>
      <button className="sfa-toast__close" onClick={onClose}>✕</button>
    </div>
  );
};

/* ══════════════════════════════════════════════════
   CONFIRM MODAL COMPONENT
══════════════════════════════════════════════════ */
const ConfirmOrderModal = ({ order, onClose, onConfirm }) => {
  const [confirming, setConfirming] = useState(false);
  const addons = buildAddonRows(order);
  const total = n(order.total || computeTotal(order));

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirm(order);
      onClose();
    } catch (err) {
      // Error handled by parent
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="sfa-modal-overlay" onClick={onClose}>
      <div className="sfa-confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sfa-confirm-header">
          <div className="sfa-confirm-icon">🔐</div>
          <h2>Confirm Frozen Order</h2>
          <p>This will deduct stock for all add-on items</p>
        </div>

        <div className="sfa-confirm-body">
          <div className="sfa-confirm-info">
            <div className="sfa-confirm-info-row">
              <span className="sfa-confirm-label">Customer</span>
              <strong>{order.name}</strong>
            </div>
            <div className="sfa-confirm-info-row">
              <span className="sfa-confirm-label">Theater</span>
              <strong>{order.theater}</strong>
            </div>
            <div className="sfa-confirm-info-row">
              <span className="sfa-confirm-label">Event Date</span>
              <strong>{order.date}</strong>
            </div>
            <div className="sfa-confirm-info-row">
              <span className="sfa-confirm-label">Total Amount</span>
              <strong className="sfa-confirm-amount">{money(total)}</strong>
            </div>
          </div>

          {addons.length > 0 && (
            <div className="sfa-confirm-addons">
              <h4>📦 Add-on Items (Stock Will Be Reduced)</h4>
              <div className="sfa-confirm-addons-list">
                {addons.map((a, idx) => (
                  <div key={idx} className="sfa-confirm-addon-item">
                    <span className="sfa-confirm-addon-emoji">{a.emoji}</span>
                    <span className="sfa-confirm-addon-name">{a.name}</span>
                    <span className="sfa-confirm-addon-qty">
                      Qty: {safeQty(a)}
                    </span>
                    <span className="sfa-confirm-addon-price">{money(itemTotal(a))}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {addons.length === 0 && (
            <div className="sfa-confirm-no-addons">
              ℹ️ No add-on items in this order. No stock will be deducted.
            </div>
          )}

          <div className="sfa-confirm-warning">
            ⚠️ This action cannot be undone. Stock will be permanently reduced.
          </div>
        </div>

        <div className="sfa-confirm-footer">
          <button className="sfa-btn--light" onClick={onClose} disabled={confirming}>
            Cancel
          </button>
          <button className="sfa-btn--confirm" onClick={handleConfirm} disabled={confirming}>
            {confirming ? "Confirming..." : "✅ Confirm & Reduce Stock"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════
   EDIT FROZEN ORDER MODAL (Superadmin Only)
══════════════════════════════════════════════════ */
const EditFrozenOrderModal = ({ order, onClose, onSave }) => {
  const categories = [
    { key: "cakes", label: "Cakes", emoji: "🎂", field: "cakes" },
    { key: "decorations", label: "Decorations", emoji: "🎨", field: "extraDecors" },
    { key: "gifts", label: "Gifts", emoji: "🎁", field: "gifts" },
    { key: "services", label: "Services", emoji: "🛎️", field: "services" },
  ];

  const [catalog, setCatalog] = useState({
    cakes: [],
    decorations: [],
    gifts: [],
    services: [],
  });
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [activeCategory, setActiveCategory] = useState("cakes");
  const [searchQuery, setSearchQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [editReason, setEditReason] = useState("");

  const [selected, setSelected] = useState({
    cakes: [...(order.cakes || [])],
    extraDecors: [...(order.extraDecors || [])],
    gifts: [...(order.gifts || [])].map((g) => ({ ...g, qty: safeQty(g) })),
    services: [...(order.services || [])],
  });

  useEffect(() => {
    let ignore = false;

    const fetchCatalog = async () => {
      setLoadingCatalog(true);
      try {
        const results = await Promise.all(
          categories.map((c) =>
            fetch(`${ADMIN_API}/${c.key}`, { credentials: "include" })
              .then((r) => (r.ok ? r.json() : []))
              .catch(() => [])
          )
        );

        if (ignore) return;

        const next = {};
        categories.forEach((c, i) => {
          const raw = Array.isArray(results[i])
            ? results[i]
            : Array.isArray(results[i]?.data)
            ? results[i].data
            : [];
          next[c.key] = raw.map(normalizeCatalogItem);
        });

        setCatalog(next);
      } finally {
        if (!ignore) setLoadingCatalog(false);
      }
    };

    fetchCatalog();

    return () => {
      ignore = true;
    };
  }, []);

  const active = categories.find((c) => c.key === activeCategory) || categories[0];

  const suggestions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const list = catalog[activeCategory] || [];
    return (q ? list.filter((x) => x.name.toLowerCase().includes(q)) : list).slice(0, 20);
  }, [catalog, activeCategory, searchQuery]);

  const isSelected = (field, item) =>
    (selected[field] || []).some(
      (x) => (x._id && item._id && x._id === item._id) || x.name === item.name
    );

  const toggleAddon = (catKey, item) => {
    const cat = categories.find((c) => c.key === catKey) || categories[0];
    const field = cat.field;

    setSelected((prev) => {
      const list = prev[field] || [];
      const exists = list.some(
        (x) => (x._id && item._id && x._id === item._id) || x.name === item.name
      );

      if (exists) {
        return {
          ...prev,
          [field]: list.filter((x) => x.name !== item.name && x._id !== item._id),
        };
      }

      const payload = {
        id: item.id,
        _id: item._id,
        name: item.name,
        price: n(item.price),
        ...(field === "gifts" ? { qty: 1 } : {}),
      };

      return { ...prev, [field]: [...list, payload] };
    });

    setSearchQuery("");
  };

  const changeQty = (field, name, delta) => {
    setSelected((prev) => {
      const list = prev[field] || [];

      return {
        ...prev,
        [field]: list
          .map((x) =>
            x.name === name ? { ...x, qty: Math.max(0, safeQty(x) + delta) } : x
          )
          .filter((x) => safeQty(x) > 0),
      };
    });
  };

  const removeAddon = (field, name) => {
    setSelected((prev) => ({
      ...prev,
      [field]: (prev[field] || []).filter((x) => x.name !== name),
    }));
  };

  const previewOrder = {
    ...order,
    cakes: selected.cakes,
    extraDecors: selected.extraDecors,
    gifts: selected.gifts,
    services: selected.services,
  };

  const newTotal = computeTotal(previewOrder);

  const allSelected = [
    ...selected.cakes.map((x) => ({ ...x, field: "cakes", emoji: "🎂" })),
    ...selected.extraDecors.map((x) => ({ ...x, field: "extraDecors", emoji: "🎨" })),
    ...selected.gifts.map((x) => ({ ...x, field: "gifts", emoji: "🎁" })),
    ...selected.services.map((x) => ({ ...x, field: "services", emoji: "🛎️" })),
  ];

  const handleSave = async () => {
    if (!editReason.trim()) {
      alert("Please provide a reason for editing this frozen order.");
      return;
    }

    setSaving(true);
    try {
      await onSave({
        ...previewOrder,
        total: newTotal,
        editReason: editReason.trim(),
        editedBy: "Superadmin"
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sfa-modal-overlay" onClick={onClose}>
      <div className="sfa-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sfa-modal-header">
          <div className="sfa-modal-title-block">
            <span className="sfa-big-icon">✏️</span>
            <div>
              <h2>Edit Frozen Order</h2>
              <p>
                {order.name} · {order.theater} · {order.date}
              </p>
              <small className="sfa-frozen-badge">🔒 Frozen Order - Superadmin Edit</small>
            </div>
          </div>
          <button className="sfa-icon-close" onClick={onClose}>✕</button>
        </div>

        <div className="sfa-edit-modal-body">
          <div className="sfa-edit-left-panel">
            <div className="sfa-edit-cat-tabs">
              {categories.map((cat) => (
                <button
                  key={cat.key}
                  className={`sfa-edit-cat-tab ${activeCategory === cat.key ? "active" : ""}`}
                  onClick={() => {
                    setActiveCategory(cat.key);
                    setSearchQuery("");
                  }}
                >
                  <span>{cat.emoji}</span>
                  <span>{cat.label}</span>
                  {selected[cat.field]?.length > 0 && <b>{selected[cat.field].length}</b>}
                </button>
              ))}
            </div>

            <div className="sfa-edit-search-wrap">
              <span>🔍</span>
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${active.label}...`}
                autoFocus
              />
              {searchQuery && <button onClick={() => setSearchQuery("")}>✕</button>}
            </div>

            <div className="sfa-edit-suggestions">
              {loadingCatalog ? (
                <div className="sfa-mini-state">Loading catalog...</div>
              ) : suggestions.length === 0 ? (
                <div className="sfa-mini-state">No item found</div>
              ) : (
                suggestions.map((item) => {
                  const picked = isSelected(active.field, item);

                  return (
                    <button
                      key={item._id || item.name}
                      className={`sfa-edit-suggestion-item ${picked ? "selected" : ""}`}
                      onClick={() => toggleAddon(activeCategory, item)}
                    >
                      <span className="sfa-suggestion-main">
                        <b>{active.emoji}</b>
                        {item.name}
                      </span>
                      <span className="sfa-suggestion-price">
                        {money(item.price)} <i>{picked ? "✓" : "+"}</i>
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="sfa-edit-right-panel">
            <div className="sfa-panel-head">
              <h3>Selected Items</h3>
              <span>
                {allSelected.length} item{allSelected.length !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="sfa-edit-selected-list">
              {allSelected.length === 0 ? (
                <div className="sfa-empty-mini">
                  🛒<p>No add-ons selected</p>
                </div>
              ) : (
                allSelected.map((a) => (
                  <div key={`${a.field}-${a.name}`} className="sfa-edit-selected-item">
                    <div>
                      <strong>
                        {a.emoji} {a.name}
                      </strong>
                      <small>
                        {money(a.price)}
                        {a.field === "gifts" &&
                          ` × ${safeQty(a)} = ${money(itemTotal(a))}`}
                      </small>
                    </div>

                    <div className="sfa-selected-actions">
                      {a.field === "gifts" && (
                        <div className="sfa-gift-qty">
                          <button onClick={() => changeQty(a.field, a.name, -1)}>−</button>
                          <b>{safeQty(a)}</b>
                          <button onClick={() => changeQty(a.field, a.name, 1)}>+</button>
                        </div>
                      )}
                      <button
                        className="sfa-remove-small"
                        onClick={() => removeAddon(a.field, a.name)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="sfa-edit-price-breakup">
              <h4>Price Breakdown</h4>

              <div>
                <span>Base + Decor + Guests</span>
                <b>
                  {money(
                    computeSubtotalBeforeManualAdjustments({
                      ...previewOrder,
                      cakes: [],
                      extraDecors: [],
                      gifts: [],
                      services: [],
                    })
                  )}
                </b>
              </div>

              {allSelected.map((a) => (
                <div key={`price-${a.field}-${a.name}`}>
                  <span>
                    {a.emoji} {a.name}
                    {safeQty(a) > 1 ? ` × ${safeQty(a)}` : ""}
                  </span>
                  <b>{money(itemTotal(a))}</b>
                </div>
              ))}

              {n(order.discount) > 0 && (
                <div className="negative">
                  <span>Coupon Discount</span>
                  <b>-{money(order.discount)}</b>
                </div>
              )}

              <div className="total">
                <span>New Grand Total</span>
                <b>{money(newTotal)}</b>
              </div>

              <div className="balance">
                <span>Balance Due</span>
                <b>{money(Math.max(0, newTotal - n(order.advancePaid || ADVANCE_PAID)))}</b>
              </div>
            </div>

            <div className="sfa-edit-reason">
              <label>Edit Reason (Required)</label>
              <textarea
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                placeholder="Explain why you are editing this frozen order..."
                rows="3"
              />
            </div>
          </div>
        </div>

        <div className="sfa-edit-modal-footer">
          <strong>New Total: {money(newTotal)}</strong>
          <div>
            <button className="sfa-light-btn" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className="sfa-primary-btn" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "💾 Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════
   MAIN SUPERADMIN COMPONENT
══════════════════════════════════════════════════ */
const SuperAdminFrozenOrders = () => {
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("frozenAt");
  const [sortDir, setSortDir] = useState("desc");
  const [expandedRow, setExpandedRow] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [editModal, setEditModal] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [stats, setStats] = useState({ totalFrozen: 0, totalValue: 0 });
  const [toast, setToast] = useState(null);
  const [confirmedOrders, setConfirmedOrders] = useState(() => loadConfirmedOrders());

  const searchRef = useRef(null);
  const filterLabel = formatRangeLabel(fromDate, toDate);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setApiError(null);

    try {
      const params = new URLSearchParams({
        sort: sortKey,
        dir: sortDir,
        limit: "500",
      });

      if (search.trim()) params.set("search", search.trim());
      if (fromDate) params.set("fromDate", fromDate);
      if (toDate) params.set("toDate", toDate);

      const res = await fetch(`${SUPERADMIN_API}/frozen-orders?${params.toString()}`, {
        signal: AbortSignal.timeout(10000),
      });

      if (!res.ok) throw new Error(`Server responded ${res.status}`);

      const json = await res.json();
      if (json.success === false) throw new Error(json.message || "Fetch failed");

      const data = Array.isArray(json.data) ? json.data : [];
      const normalized = data.map((o) => ({
        ...o,
        total: n(o.total || computeTotal(o)),
      }));

      setOrders(normalized);
    } catch (err) {
      setApiError(err.message);
      console.error("Error fetching frozen orders:", err);
    } finally {
      setLoading(false);
    }
  }, [search, sortDir, sortKey, fromDate, toDate]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${SUPERADMIN_API}/stats`, {
        signal: AbortSignal.timeout(5000),
      });
      if (res.ok) {
        const json = await res.json();
        if (json.success) {
          setStats(json.data);
        }
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchStats();
  }, [fetchOrders, fetchStats]);

  useEffect(() => {
    saveConfirmedOrders(confirmedOrders);
  }, [confirmedOrders]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const filtered = useMemo(() => {
    let rows = [...orders];

    rows.sort((a, b) => {
      let av = a[sortKey] ?? "";
      let bv = b[sortKey] ?? "";

      if (sortKey === "total") {
        av = n(av);
        bv = n(bv);
        return sortDir === "asc" ? av - bv : bv - av;
      }

      return sortDir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });

    return rows;
  }, [orders, sortKey, sortDir]);

  const handleSaveEdit = async (updatedOrder) => {
    const id = getOrderId(updatedOrder);

    try {
      const res = await fetch(`${SUPERADMIN_API}/frozen-orders/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cakes: updatedOrder.cakes,
          extraDecors: updatedOrder.extraDecors,
          gifts: updatedOrder.gifts,
          services: updatedOrder.services,
          total: updatedOrder.total,
          editReason: updatedOrder.editReason,
          editedBy: "Superadmin"
        }),
        signal: AbortSignal.timeout(8000),
      });

      if (!res.ok) throw new Error(`Update failed ${res.status}`);

      const json = await res.json();
      if (json.success) {
        setOrders((prev) =>
          prev.map((o) => (getOrderId(o) === id ? { ...o, ...json.data } : o))
        );
        fetchStats();
      }
    } catch (err) {
      alert(`Failed to update: ${err.message}`);
      throw err;
    }
  };

  // ✅ FIXED: Targets the exact registered backend database mounting endpoint (RESERVATIONS_API)
  const handleToggleCorrect = async (id, currentStatus) => {
    // Optimistic UI Update
    setOrders((prev) =>
      prev.map((o) => (getOrderId(o) === id ? { ...o, corrected: !currentStatus } : o))
    );

    try {
      const res = await fetch(`${RESERVATIONS_API}/frozen-orders/${id}/correct`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ corrected: !currentStatus }),
        signal: AbortSignal.timeout(6000),
      });

      if (!res.ok) throw new Error("Correction sync failed on database");
      
      const json = await res.json();
      if (json.success) {
        setToast({ message: `Row color state permanently updated!`, type: "success" });
      }
    } catch (err) {
      // Revert UI Update on network failures
      setOrders((prev) =>
        prev.map((o) => (getOrderId(o) === id ? { ...o, corrected: currentStatus } : o))
      );
      setToast({ message: `Failed to save color state: ${err.message}`, type: "error" });
    }
  };

  /* ════════════════════════════════════════════════════
     CONFIRM ORDER — PERMANENT MongoDB stock reduction
  ════════════════════════════════════════════════════ */
  const handleConfirmOrder = async (order) => {
    const id = getOrderId(order);
    const addons = buildAddonRows(order);

    // Skip non-stock accessories (3D glasses etc.) unless you stock them by name
    const deductPayload = addons
      .filter((a) => {
        const cat = addonCategoryToStock(a.category);
        return !!cat; // only cakes/decorations/gifts/services
      })
      .map((a) => ({
        name: a.name,
        qty: safeQty(a),
        category: addonCategoryToStock(a.category),
        _id: a._id || a.id || undefined,
        emoji: a.emoji,
      }));

    try {
      let deductionLog = [];
      let unmatchedItems = [];

      /* ── Step 1: PERMANENT deduct in MongoDB ── */
      if (deductPayload.length > 0) {
        const res = await fetch(`${STOCK_API}/deduct-for-order`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            orderId: id,
            orderName: order.name || "Unknown",
            note: `Frozen order confirmed: ${order.name || ""} · ${order.theater || ""} · ${order.date || ""}`,
            items: deductPayload,
          }),
          signal: AbortSignal.timeout(15000),
        });

        const json = await res.json().catch(() => ({}));

        if (!res.ok || json.success === false) {
          throw new Error(json.message || `Stock deduction failed (${res.status})`);
        }

        deductionLog = (json.data?.deducted || []).map((d) => ({
          name: d.name,
          emoji: d.emoji || "📦",
          category: d.category,
          qty: d.qty,
          oldStock: d.oldStock,
          newStock: d.newStock,
        }));
        unmatchedItems = json.data?.unmatched || [];
      }

      /* ── Step 2: Refresh localStorage cache from server list ── */
      try {
        const stockRes = await fetch(`${STOCK_API}/all`, {
          credentials: "include",
          signal: AbortSignal.timeout(10000),
        });
        if (stockRes.ok) {
          const stockJson = await stockRes.json();
          if (Array.isArray(stockJson.data)) {
            saveLocalStock(
              stockJson.data.map((item) => ({
                ...item,
                id: String(item._id || item.id),
                _id: String(item._id || item.id),
              }))
            );
          }
        }
      } catch (e) {
        console.warn("Could not refresh local stock cache:", e.message);
      }

      /* ── Step 3: Log event for Stock Management UI ── */
      const stockEvent = {
        orderId: id,
        orderName: order.name || "Unknown",
        theater: order.theater || "",
        eventDate: order.date || "",
        timestamp: Date.now(),
        items: deductionLog,
        unmatched: unmatchedItems,
      };

      const events = loadStockEvents();
      events.push(stockEvent);
      saveStockEvents(events);

      /* ── Step 4: Notify Stock Management (same tab + other tabs via storage) ── */
      window.dispatchEvent(
        new CustomEvent("stockUpdated", {
          detail: { type: "orderConfirmed", event: stockEvent },
        })
      );

      /* ── Step 5: Mark order confirmed on backend ── */
      try {
        await fetch(`${SUPERADMIN_API}/frozen-orders/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            confirmed: true,
            confirmedAt: new Date().toISOString(),
            confirmedBy: "Superadmin",
          }),
          signal: AbortSignal.timeout(5000),
        });
      } catch (backendErr) {
        console.log("Backend order status update skipped:", backendErr.message);
      }

      /* ── Step 6: Local UI ── */
      setConfirmedOrders((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });

      setOrders((prev) =>
        prev.map((o) =>
          getOrderId(o) === id
            ? { ...o, confirmed: true, confirmedAt: new Date().toISOString() }
            : o
        )
      );
      setConfirmModal(null);

      let toastMsg = `Order confirmed for ${order.name}!`;
      if (deductionLog.length > 0) {
        toastMsg += ` Stock reduced for ${deductionLog.length} item(s).`;
      }
      if (unmatchedItems.length > 0) {
        toastMsg += ` ⚠️ Not found in stock: ${unmatchedItems.join(", ")}`;
      }
      if (deductPayload.length === 0) {
        toastMsg = `Order confirmed for ${order.name}! No stock add-ons to deduct.`;
      }

      setToast({
        message: toastMsg,
        type: unmatchedItems.length > 0 && deductionLog.length === 0 ? "warning" : "success",
      });
      fetchStats();
    } catch (err) {
      console.error("Error confirming order:", err);
      setToast({
        message: `Failed to confirm order: ${err.message}`,
        type: "error",
      });
      throw err;
    }
  };

  const handleUnfreeze = async (order) => {
    const ok = window.confirm(
      `Unfreeze this order for ${order.name}? This will move it back to active reservations.`
    );
    if (!ok) return;

    try {
      const id = getOrderId(order);
      const res = await fetch(`${SUPERADMIN_API}/frozen-orders/${id}/unfreeze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: AbortSignal.timeout(8000),
      });

      if (!res.ok) throw new Error(`Unfreeze failed ${res.status}`);

      const json = await res.json();
      if (json.success) {
        setOrders((prev) => prev.filter((o) => getOrderId(o) !== id));
        
        setConfirmedOrders((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        
        fetchStats();
        setToast({ message: "Order unfrozen successfully!", type: "success" });
      }
    } catch (err) {
      setToast({ message: `Failed to unfreeze: ${err.message}`, type: "error" });
    }
  };

  const handleDelete = async (order) => {
    setDeleting(true);

    try {
      const id = getOrderId(order);
      const res = await fetch(`${SUPERADMIN_API}/frozen-orders/${id}`, {
        method: "DELETE",
        signal: AbortSignal.timeout(8000),
      });

      if (!res.ok) throw new Error(`Delete failed ${res.status}`);

      setOrders((prev) => prev.filter((o) => getOrderId(o) !== id));
      setDeleteConfirm(null);
      setExpandedRow(null);
      
      setConfirmedOrders((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      
      fetchStats();
      setToast({ message: "Order deleted successfully!", type: "success" });
    } catch (err) {
      setToast({ message: `Delete failed: ${err.message}`, type: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const totalFrozenValue = filtered.reduce((a, o) => a + n(o.total || computeTotal(o)), 0);
  const totalAdvance = filtered.reduce((a, o) => a + n(o.advancePaid || ADVANCE_PAID), 0);

  const isSearchActive = search.trim().length > 0;
  const isDateFiltered = Boolean(fromDate || toDate);

  const Th = ({ k, label }) => (
    <th
      className={`sfa-th ${sortKey === k ? "sfa-th-active" : ""}`}
      onClick={() => handleSort(k)}
    >
      {label} <span>{sortKey === k ? (sortDir === "asc" ? "↑" : "↓") : "⇅"}</span>
    </th>
  );

  if (loading) {
    return (
      <div className="sfa-loading">
        <div className="sfa-spinner" />
        <p>Loading frozen orders...</p>
      </div>
    );
  }

  return (
    <div className="sfa-page">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {confirmModal && (
        <ConfirmOrderModal
          order={confirmModal}
          onClose={() => setConfirmModal(null)}
          onConfirm={handleConfirmOrder}
        />
      )}

      <div className="sfa-header">
        <div className="sfa-header-left">
          <div className="sfa-logo" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
            🔐
          </div>

          <div>
            <h1>Superadmin - Frozen Orders</h1>
            <p>
              Manage frozen bookings · {filtered.length} frozen order{filtered.length !== 1 ? "s" : ""}{" "}
              <span className="sfa-live-badge superadmin">👑 Superadmin Access</span>
            </p>
          </div>
        </div>

        <div className="sfa-header-right">
          <div className="sfa-search-wrap">
            <span>🔍</span>
            <input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, theater, phone, occasion..."
            />
            {search && (
              <button
                onClick={() => {
                  setSearch("");
                  searchRef.current?.focus();
                }}
              >
                ✕
              </button>
            )}
          </div>

          <button
            className="sfa-refresh-btn"
            onClick={() => {
              fetchOrders();
              fetchStats();
            }}
          >
            🔄 Refresh
          </button>

          <button
            className="sfa-stock-btn"
            onClick={() => navigate("/superadmin/stock-management")}
          >
            📦 Stock Management
          </button>

          <button
            className="sfa-admin-btn"
            onClick={() => navigate("/admin/orders")}
          >
            ← Back to Admin
          </button>
        </div>
      </div>

      {apiError && (
        <div className="sfa-error-banner">
          <span>⚠️ Could not reach server ({apiError}).</span>
          <button onClick={fetchOrders}>Retry</button>
        </div>
      )}

      <div className="sfa-filter-bar">
        <div className="sfa-date-range-group">
          <strong>📅 Frozen Date Range</strong>

          <label>
            From{" "}
            <input
              type="date"
              value={toInputVal(fromDate)}
              max={toInputVal(toDate) || undefined}
              onChange={(e) => {
                setFromDate(fromInputVal(e.target.value));
                setExpandedRow(null);
              }}
            />
          </label>

          <span>→</span>

          <label>
            To{" "}
            <input
              type="date"
              value={toInputVal(toDate)}
              min={toInputVal(fromDate) || undefined}
              onChange={(e) => {
                setToDate(fromInputVal(e.target.value));
                setExpandedRow(null);
              }}
            />
          </label>

          {(fromDate || toDate) && (
            <button
              onClick={() => {
                setFromDate("");
                setToDate("");
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="sfa-stats">
        <div className="sfa-stat-card purple">
          <span>🔒</span>
          <div>
            <b>{stats.totalFrozen}</b>
            <p>Total Frozen Orders</p>
          </div>
        </div>

        <div className="sfa-stat-card blue">
          <span>💰</span>
          <div>
            <b>{money(stats.totalValue)}</b>
            <p>Total Frozen Value</p>
          </div>
        </div>

        <div className="sfa-stat-card green">
          <span>📋</span>
          <div>
            <b>{filtered.length}</b>
            <p>Filtered Orders</p>
          </div>
        </div>

        <div className="sfa-stat-card orange">
          <span>💵</span>
          <div>
            <b>{money(totalFrozenValue)}</b>
            <p>Filtered Value</p>
          </div>
        </div>

        <div className="sfa-stat-card red">
          <span>✅</span>
          <div>
            <b>{confirmedOrders.size}</b>
            <p>Confirmed</p>
          </div>
        </div>
      </div>

      <div className="sfa-toolbar">
        <span>
          <b>{filtered.length}</b> frozen order{filtered.length !== 1 ? "s" : ""}
          {isDateFiltered ? (
            <>
              {" "}
              in <b>{filterLabel}</b>
            </>
          ) : null}
          {isSearchActive ? (
            <>
              {" "}
              matching <b>"{search}"</b>
            </>
          ) : null}
        </span>

        <span>🔒 Only Superadmin can edit frozen orders</span>
      </div>

      <div className="sfa-table-outer">
        {filtered.length === 0 ? (
          <div className="sfa-empty">
            <div>{isSearchActive ? "🔍" : "📭"}</div>
            <h3>{isSearchActive ? "No results found" : "No frozen orders"}</h3>
            <p>
              {isSearchActive
                ? `No frozen orders match "${search}".`
                : isDateFiltered
                ? `No frozen orders for ${filterLabel}.`
                : "No orders have been frozen yet."}
            </p>

            <button
              className="sfa-primary-btn"
              onClick={() => navigate("/admin/orders")}
              style={{ marginTop: 16 }}
            >
              ← Go to Admin Orders
            </button>
          </div>
        ) : (
          <div className="sfa-table-scroll">
            <table className="sfa-table">
              <thead>
                <tr>
                  <th className="sfa-th">#</th>
                  <Th k="frozenAt" label="Frozen On" />
                  <Th k="name" label="Customer" />
                  <Th k="theater" label="Theater" />
                  <th className="sfa-th">Event Date</th>
                  <th className="sfa-th">Slot</th>
                  <th className="sfa-th">Guests</th>
                  <Th k="total" label="Amount" />
                  <th className="sfa-th">Status</th>
                  <th className="sfa-th">Add-ons</th>
                  <th className="sfa-th action">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((o, i) => {
                  const id = getOrderId(o) || `${i}`;
                  const addons = buildAddonRows(o);
                  const isExpanded = expandedRow === i;
                  const total = n(o.total || computeTotal(o));
                  const isConfirmed = confirmedOrders.has(id) || o?.confirmed;
                  
                  // Read correction status dynamically from order item
                  const isCorrected = o.corrected;

                  return (
                    <React.Fragment key={id}>
                      <tr
                        className={`sfa-row ${isExpanded ? "expanded" : ""} ${isConfirmed ? "confirmed" : ""} ${isCorrected ? "corrected" : ""}`}
                        onClick={() => setExpandedRow(isExpanded ? null : i)}
                      >
                        <td>
                          <span className="sfa-row-num">{i + 1}</span>
                          <span title="Frozen"> 🔒</span>
                        </td>

                        <td>
                          <span className="sfa-date-ts">{formatTS(o.frozenAt)}</span>
                        </td>

                        <td>
                          <div className="sfa-customer">
                            <div className="sfa-avatar">
                              {(o.name || "?")[0].toUpperCase()}
                            </div>
                            <div>
                              <b>{o.name || "—"}</b>
                              <span>{o.whatsapp || "—"}</span>
                              {o.celebrationName && <small>🎉 {o.celebrationName}</small>}
                            </div>
                          </div>
                        </td>

                        <td>
                          <span className="sfa-theater-pill">{o.theater || "—"}</span>
                        </td>

                        <td>
                          <b>{o.date || "—"}</b>
                        </td>

                        <td>{o.timeSlot || "—"}</td>

                        <td>
                          <span>👤 {n(o.adults)}</span>
                          {n(o.kids) > 0 && <span> 🧒 {n(o.kids)}</span>}
                          {n(o.glassesCount) > 0 && <span> 🥽 {n(o.glassesCount)}</span>}
                        </td>

                        <td>
                          <div className="sfa-amount-cell">
                            <b>{money(total)}</b>
                            <span>{money(o.advancePaid || ADVANCE_PAID)} advance</span>
                            {o.paymentInfo && (
                              <small>
                                Settled {money(o.paymentInfo.totalPaid || o.paymentInfo.finalAmount)}
                              </small>
                            )}
                          </div>
                        </td>

                        <td>
                          {isConfirmed ? (
                            <span className="sfa-confirmed-badge">✅ Confirmed</span>
                          ) : (
                            <span className="sfa-frozen-badge-large">🔒 Frozen</span>
                          )}
                        </td>

                        <td>
                          {addons.length === 0 ? (
                            <span className="sfa-no-addon">—</span>
                          ) : (
                            <div className="sfa-addon-chips">
                              {addons.slice(0, 2).map((a) => (
                                <span key={`${a.name}-${a.category}`}>
                                  {a.emoji} {a.name}
                                  {safeQty(a) > 1 ? ` ×${safeQty(a)}` : ""}
                                </span>
                              ))}
                              {addons.length > 2 && <b>+{addons.length - 2}</b>}
                            </div>
                          )}
                        </td>

                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="sfa-actions">
                            {!isConfirmed && (
                              <button
                                className="sfa-action-btn sfa-action-btn--confirm"
                                title="Confirm order & reduce stock"
                                onClick={() => setConfirmModal(o)}
                              >
                                ✅ Confirm
                              </button>
                            )}
                            
                            {/* ✔️ Correct button toggling the server database status dynamically */}
                            <button 
                              className={`sfa-action-btn sfa-action-btn--correct ${isCorrected ? "active" : ""}`}
                              title="Mark order details correct"
                              onClick={() => handleToggleCorrect(id, isCorrected)}
                            >
                              ✔️ Correct
                            </button>

                            <button className="sfa-action-btn sfa-action-btn--edit" onClick={() => setEditModal(o)}>
                              ✏️
                            </button>
                            
                            <button className="sfa-action-btn sfa-action-btn--delete" onClick={() => setDeleteConfirm(o)}>
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="sfa-expand-row">
                          <td colSpan="11">
                            <div className="sfa-expand-body">
                              <div className="sfa-frozen-banner">
                                🔒 <b>This is a frozen order.</b>{" "}
                                Frozen on {formatTS(o.frozenAt)} by {o.frozenBy || "Admin"}
                                {isConfirmed && (
                                  <span className="sfa-confirmed-inline"> · ✅ Confirmed on {formatTS(o.confirmedAt)}</span>
                                )}
                              </div>

                              <div className="sfa-expand-grid">
                                <InfoPanel
                                  title="👤 Customer Details"
                                  rows={[
                                    ["Full Name", o.name],
                                    ["Celebrating", o.celebrationName || "—"],
                                    ["WhatsApp", o.whatsapp],
                                    ["Email", o.email || "—"],
                                    ["Order ID", id],
                                    ["Original ID", o.originalId],
                                  ]}
                                />

                                <InfoPanel
                                  title="🏟️ Booking Details"
                                  rows={[
                                    ["Theater", o.theater],
                                    ["Event Date", o.date],
                                    ["Time Slot", o.timeSlot],
                                    ["Occasion", o.occasion],
                                    [
                                      "Guests",
                                      `${n(o.adults)} Adults${
                                        n(o.kids) ? `, ${n(o.kids)} Kids` : ""
                                      }`,
                                    ],
                                    ["Frozen At", formatTS(o.frozenAt)],
                                    ["Frozen By", o.frozenBy || "Admin"],
                                  ]}
                                />

                                <PricePanel order={o} />
                              </div>

                              {o.editHistory && o.editHistory.length > 0 && (
                                <div className="sfa-edit-history">
                                  <h4>📝 Edit History</h4>
                                  {o.editHistory.map((edit, idx) => (
                                    <div key={idx} className="sfa-edit-history-item">
                                      <div>
                                        <b>{formatTS(edit.editedAt)}</b>
                                        <span>by {edit.editedBy}</span>
                                      </div>
                                      <p>{edit.reason}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editModal && (
        <EditFrozenOrderModal
          order={editModal}
          onClose={() => setEditModal(null)}
          onSave={handleSaveEdit}
        />
      )}

      {deleteConfirm && (
        <div className="sfa-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="sfa-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sfa-delete-icon">🗑️</div>
            <h3>Delete frozen order permanently?</h3>
            <p>
              You are about to permanently delete <b>{deleteConfirm.name}</b>'s frozen booking
              for <b>{deleteConfirm.theater}</b> on <b>{deleteConfirm.date}</b>.
            </p>
            <p className="sfa-delete-warning">
              ⚠️ This action cannot be undone!
            </p>

            <div>
              <button
                className="sfa-light-btn"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="sfa-danger-btn"
                onClick={() => handleDelete(deleteConfirm)}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Yes, Delete Permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoPanel = ({ title, rows }) => (
  <div className="sfa-expand-section">
    <h4>{title}</h4>
    {rows.map(([label, value]) => (
      <div className="sfa-expand-row-item" key={label}>
        <span>{label}</span>
        <b>{value || "—"}</b>
      </div>
    ))}
  </div>
);

const PricePanel = ({ order }) => {
  const addons = buildAddonRows(order).filter((a) => a.category !== "Extra Add-on");
  const extraAddons = getAddonArrays(order).extraAddons; // ✅ pulled straight from Staff Management data
  const total = n(order.total || computeTotal(order));

  return (
    <div className="sfa-expand-section">
      <h4>💰 Pricing Breakdown</h4>

      <div className="sfa-expand-row-item">
        <span>Base Price</span>
        <b>{money(order.basePrice)}</b>
      </div>

      <div className="sfa-expand-row-item">
        <span>Fixed Decor</span>
        <b>{money(order.fixedDecor || DEFAULT_FIXED_DECOR)}</b>
      </div>

      {n(order.extraAdults) > 0 && (
        <div className="sfa-expand-row-item">
          <span>Extra Adults ({n(order.extraAdults)})</span>
          <b>{money(n(order.extraAdults) * n(order.extraAdultPrice || DEFAULT_EXTRA_ADULT_PRICE))}</b>
        </div>
      )}

      {n(order.kids) > 0 && (
        <div className="sfa-expand-row-item">
          <span>Kids ({n(order.kids)})</span>
          <b>{money(n(order.kids) * n(order.kidPrice || DEFAULT_KID_PRICE))}</b>
        </div>
      )}

      {addons.map((a) => (
        <div className="sfa-expand-row-item" key={`${a.category}-${a.name}`}>
          <span>
            {a.emoji} {a.name}
            {safeQty(a) > 1 ? ` × ${safeQty(a)}` : ""}
          </span>
          <b>{money(itemTotal(a))}</b>
        </div>
      ))}

      {/* ✅ NEW — Extra Add-ons section (data pushed here from Staff Management's "⭐ Extra Add-ons" modal) */}
      {extraAddons.length > 0 && (
        <>
          <h4 className="sfa-subsection-title">⭐ Extra Add-ons</h4>
          {extraAddons.map((a, idx) => (
            <div className="sfa-expand-row-item" key={`extra-addon-${a.name || idx}`}>
              <span>
                {a.emoji || "⭐"} {a.name}
                {safeQty(a) > 1 ? ` × ${safeQty(a)}` : ""}
              </span>
              <b>{money(itemTotal(a))}</b>
            </div>
          ))}
          <div className="sfa-expand-row-item">
            <span>Extra Add-ons Subtotal</span>
            <b>{money(extraAddons.reduce((sum, x) => sum + itemTotal(x), 0))}</b>
          </div>
        </>
      )}

      {n(order.discount) > 0 && (
        <div className="sfa-expand-row-item negative">
          <span>Coupon Discount</span>
          <b>-{money(order.discount)}</b>
        </div>
      )}

      <div className="sfa-expand-row-item total">
        <span>Grand Total</span>
        <b>{money(total)}</b>
      </div>

      <div className="sfa-expand-row-item advance">
        <span>Advance Paid</span>
        <b>{money(order.advancePaid || ADVANCE_PAID)}</b>
      </div>

      {order.paymentInfo && (
        <>
          <div className="sfa-expand-row-item">
            <span>Settlement Status</span>
            <b>✅ Completed</b>
          </div>

          <div className="sfa-expand-row-item total">
            <span>Final Amount Paid</span>
            <b>{money(order.paymentInfo.finalAmount)}</b>
          </div>
        </>
      )}
    </div>
  );
};

export default SuperAdminFrozenOrders;